import { HttpParams } from '@angular/common/http';
import { ApiService } from '@libs/core/base-config';
import { omitUndefinedValues } from '@libs/core/lib/utils/utils';
import {
  Dtos,
  getSubOrderChatResponseSchema,
  orderProductForShippingResponseSchema,
  orderSchema,
  ordersListCustomersResponseSchema,
  ordersListResponseSchema,
} from '@libs/mtjr/shared/generated/types';
import { map } from 'rxjs';

export function calculateShippingCost(
  apiService: ApiService,
  { orderProducts, customerAddress }: Dtos.CalculateShippingCostBody,
) {
  return apiService.http
    .post(`${apiService.baseUrl}/api/v1/orders/calculate-shipping-cost`, { orderProducts, customerAddress })
    .pipe(map((data) => orderProductForShippingResponseSchema().parse(data)));
}

export function getSubOrderChat(apiService: ApiService, { orderId, subOrderId }: Dtos.GetSubOrderChatPathParams) {
  return apiService.http
    .get(`${apiService.baseUrl}/api/v1/orders/get-sub-order-chat/${orderId}/${subOrderId}`, {})
    .pipe(map((data) => getSubOrderChatResponseSchema().parse(data)));
}

export function getOrderById(apiService: ApiService, { id }: Dtos.GetOrderByIdPathParams) {
  return apiService.http
    .get(`${apiService.baseUrl}/api/v1/orders/get-order-by-id/${id}`, {})
    .pipe(map((data) => orderSchema().parse(data)));
}

export function ordersList(apiService: ApiService, queryParams: Dtos.OrdersListQueryParams) {
  const params = new HttpParams({ fromObject: omitUndefinedValues(queryParams) });
  return apiService.http
    .get(`${apiService.baseUrl}/api/v1/orders/orders-list`, { params })
    .pipe(map((data) => ordersListResponseSchema().parse(data)));
}

export function ordersListCustomers(
  apiService: ApiService,
  queryParams: Dtos.OrdersListOrdersListCustomersQueryParams,
) {
  const params = new HttpParams({ fromObject: omitUndefinedValues(queryParams) });
  return apiService.http
    .get(`${apiService.baseUrl}/api/v1/orders/orders-list-customers`, { params })
    .pipe(map((data) => ordersListCustomersResponseSchema().parse(data)));
}

export function markOrderAsReadyForShipping(
  apiService: ApiService,
  { orderId, subOrderId }: Dtos.MarkOrderAsReadyForShippingPathParams,
) {
  return apiService.http.post<void>(
    `${apiService.baseUrl}/api/v1/orders/mark-order-as-ready-for-shipping/${orderId}/${subOrderId}`,
    {},
  );
}

export function markOrderAsProcessing(
  apiService: ApiService,
  { orderId, subOrderId }: Dtos.MarkOrderAsProcessingPathParams,
) {
  return apiService.http.post<void>(
    `${apiService.baseUrl}/api/v1/orders/mark-order-as-processing/${orderId}/${subOrderId}`,
    {},
  );
}

export function cancelOrder(
  apiService: ApiService,
  { reason }: Dtos.CancelOrderBody,
  { orderId, subOrderId }: Dtos.CancelOrderPathParams,
) {
  return apiService.http.post<void>(`${apiService.baseUrl}/api/v1/orders/cancel-order/${orderId}/${subOrderId}`, {
    reason,
  });
}

export function sendChatMessage(
  apiService: ApiService,
  { text, attachment }: Dtos.SendChatMessageBody,
  { orderId, subOrderId }: Dtos.SendChatMessagePathParams,
) {
  return apiService.http.post<void>(`${apiService.baseUrl}/api/v1/orders/send-chat-message/${orderId}/${subOrderId}`, {
    text,
    attachment,
  });
}

export function markChatMessageAsSeen(
  apiService: ApiService,
  { orderId, subOrderId, messageId }: Dtos.MarkChatMessageAsSeenPathParams,
) {
  return apiService.http.post<void>(
    `${apiService.baseUrl}/api/v1/orders/mark-chat-message-as-seen/${orderId}/${subOrderId}/${messageId}`,
    {},
  );
}

export function updateChatMessage(
  apiService: ApiService,
  { text, attachment }: Dtos.UpdateChatMessageBody,
  { orderId, subOrderId, messageId }: Dtos.UpdateChatMessagePathParams,
) {
  return apiService.http.post<void>(
    `${apiService.baseUrl}/api/v1/orders/update-chat-message/${orderId}/${subOrderId}/${messageId}`,
    { text, attachment },
  );
}

export function getPublicOrder(apiService: ApiService, { orderId }: Dtos.GetPublicOrderPathParams) {
  return apiService.http
    .get(`${apiService.baseUrl}/api/v1/orders/get-public-order/${orderId}`, {})
    .pipe(map((data) => orderSchema().parse(data)));
}

export function placeOrder(
  apiService: ApiService,
  { orderProducts, address, paymentMethod, idempotencyKey, notes }: Dtos.PlaceOrderBody,
) {
  return apiService.http
    .post(`${apiService.baseUrl}/api/v1/orders/place-order`, {
      orderProducts,
      address,
      paymentMethod,
      idempotencyKey,
      notes,
    })
    .pipe(map((data) => orderSchema().parse(data)));
}

export function buyNow(
  apiService: ApiService,
  { productId, variationId, finalPrice, paymentMethod, idempotencyKey }: Dtos.BuyNowBody,
) {
  return apiService.http
    .post(`${apiService.baseUrl}/api/v1/orders/buy-now`, {
      productId,
      variationId,
      finalPrice,
      paymentMethod,
      idempotencyKey,
    })
    .pipe(map((data) => orderSchema().parse(data)));
}
